/**
 * WebSocket Context Provider
 * Provides global WebSocket connection management
 */
import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import {
  getWebSocketClient,
  resetWebSocketClient,
  type WebSocketClient,
  type SubscriptionType,
  type MeasurementData,
  type EnvironmentalIndexData,
  type AlertData,
  type WebSocketMessage,
} from '../api/websocket';

export type WebSocketStatus = 'disconnected' | 'connecting' | 'connected';

interface WebSocketContextValue {
  status: WebSocketStatus;
  clientId: string | null;
  isConnected: boolean;
  connect: () => void;
  disconnect: () => void;
  subscribe: (type: SubscriptionType, entityId: number) => void;
  unsubscribe: (type: SubscriptionType, entityId: number) => void;
  lastMessage: WebSocketMessage | null;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

interface WebSocketProviderProps {
  children: ReactNode;
  enabled?: boolean;
}

export function WebSocketProvider({ children, enabled = true }: WebSocketProviderProps) {
  const { accessToken, isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<WebSocketStatus>('disconnected');
  const [clientId, setClientId] = useState<string | null>(null);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [wsClient, setWsClient] = useState<WebSocketClient | null>(null);

  // Initialize WebSocket client when authenticated
  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      // Clean up if disabled or not authenticated
      if (wsClient) {
        wsClient.disconnect();
        resetWebSocketClient();
        setWsClient(null);
        setStatus('disconnected');
        setClientId(null);
      }
      return;
    }

    const client = getWebSocketClient({
      token: accessToken || undefined,
      onConnected: (id) => {
        setStatus('connected');
        setClientId(id);
        console.log('[WebSocket Context] Connected with client ID:', id);
      },
      onDisconnected: () => {
        setStatus('disconnected');
        setClientId(null);
        console.log('[WebSocket Context] Disconnected');
      },
      onError: (error) => {
        console.error('[WebSocket Context] Error:', error);
        setStatus('disconnected');
      },
      onMessage: (message) => {
        setLastMessage(message);
      },
      onMeasurement: (data: MeasurementData) => {
        // Invalidate relevant queries
        queryClient.invalidateQueries({ queryKey: ['sensor-latest', data.sensor_id] });
        queryClient.invalidateQueries({ queryKey: ['room-environmental', data.room_id] });
        queryClient.invalidateQueries({ queryKey: ['sensor-measurements', data.sensor_id] });
      },
      onEnvironmentalIndex: (data: EnvironmentalIndexData) => {
        // Invalidate room-related queries
        queryClient.invalidateQueries({ queryKey: ['room-environmental', data.room_id] });
        queryClient.invalidateQueries({ queryKey: ['room-statuses'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-overview'] });
      },
      onAlert: (data: AlertData) => {
        // Invalidate alert-related queries
        queryClient.invalidateQueries({ queryKey: ['recent-alerts'] });
        queryClient.invalidateQueries({ queryKey: ['unread-notification-count'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard-overview'] });
        queryClient.invalidateQueries({ queryKey: ['alert-instances'] });

        // Could trigger a toast notification here
        console.log('[WebSocket Context] New alert:', data);
      },
    });

    setWsClient(client);
    setStatus('connecting');
    client.connect();

    return () => {
      client.disconnect();
      resetWebSocketClient();
      setWsClient(null);
    };
  }, [enabled, isAuthenticated, accessToken, queryClient]);

  const connect = useCallback(() => {
    if (wsClient && !wsClient.isConnected()) {
      setStatus('connecting');
      wsClient.connect();
    }
  }, [wsClient]);

  const disconnect = useCallback(() => {
    wsClient?.disconnect();
    setStatus('disconnected');
  }, [wsClient]);

  const subscribe = useCallback((type: SubscriptionType, entityId: number) => {
    wsClient?.subscribe(type, entityId);
  }, [wsClient]);

  const unsubscribe = useCallback((type: SubscriptionType, entityId: number) => {
    wsClient?.unsubscribe(type, entityId);
  }, [wsClient]);

  const value: WebSocketContextValue = {
    status,
    clientId,
    isConnected: status === 'connected',
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    lastMessage,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * Hook to access WebSocket context
 */
export function useWebSocketContext(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
}

/**
 * Hook to get WebSocket connection status
 */
export function useWebSocketStatus() {
  const { status, isConnected, clientId } = useWebSocketContext();
  return { status, isConnected, clientId };
}

/**
 * Hook to subscribe to entity updates
 */
export function useWebSocketSubscription(
  type: SubscriptionType,
  entityId: number | undefined,
  options: { enabled?: boolean } = {}
) {
  const { enabled = true } = options;
  const { subscribe, unsubscribe, isConnected } = useWebSocketContext();

  useEffect(() => {
    if (!enabled || !entityId || !isConnected) {
      return;
    }

    subscribe(type, entityId);

    return () => {
      unsubscribe(type, entityId);
    };
  }, [type, entityId, enabled, isConnected, subscribe, unsubscribe]);
}

// Re-export types
export type { SubscriptionType, MeasurementData, EnvironmentalIndexData, AlertData, WebSocketMessage };
